import { useEffect, useState } from 'react';
import terminalApi from 'repos/tekoApis/terminalApi';
import {
  ITerminal,
  IService,
  IServiceCostSheet,
  ITerminalService,
  IPagination,
  ITerminalSearch,
  AnyObject,
  IGetTerminalNoPayParams,
  ITerminalNoPay,
  IVerifyTerminalNoPayPayload,
  IGetTerminalWaitingGrantedBank,
  IGetTerminalWaitingGrantedBankParams,
} from 'interfaces';
import { commonConstants, terminalConstants } from 'constants/index';
import { arrayHelpers, paginationHelpers } from 'helpers';
import { EOperators } from 'constants/operators';
import { EDirection } from 'constants/common';
import terminalServiceApi from 'repos/tekoApis/terminalServiceApi';
import { defaultPage } from 'constants/pagination';
import moment from 'moment';
import { EService } from 'constants/service';
import { terminalService } from 'services';
import { PaginationProps } from 'antd/es/pagination';
import { pickBy } from 'lodash';

const { DEFAULT_SIZE_CHANGER } = commonConstants;
const { DEFAULT_QUERY_TER_NOPAY_PARAMS } = terminalConstants;
const { mapPagerApiToAntdPager } = paginationHelpers;

const useTerminal = (defaultParams: ITerminalSearch) => {
  const [fetching, setFetching] = useState<boolean>(false);
  const [params, setParams] = useState<ITerminalSearch>(defaultParams);
  const [pagination, setPagination] = useState<IPagination>({
    ...DEFAULT_SIZE_CHANGER,
  });
  const [terminal, setTerminal] = useState<any>(false);

  const fetch = async () => {
    try {
      setFetching(true);
      const result = await terminalApi.getListTerminalWaiting(params);
      const { data, metadata } = result;

      setTerminal(data);

      let pager = { ...pagination };
      pager = arrayHelpers.mapPagerApiToAntdPager(metadata, pager);
      setPagination(pager);
    } finally {
      setFetching(false);
    }
  };

  const handleTableChange = (pagination: IPagination) => {
    const newQueryParams = {
      ...params,
      page: pagination.current,
      page_size: pagination.pageSize,
    };
    setParams(newQueryParams);
  };

  useEffect(() => {
    fetch().then();
  }, [params]);

  return {
    fetching,
    pagination,
    setFetching,
    terminal,
    params,
    setParams,
    handleTableChange,
  };
};

const useTerminalWaitingFilter = (
  params: ITerminalSearch,
  setParams: (params: ITerminalSearch) => void,
  statusByRole: { [key: string]: any }
) => {
  const [loading, setLoading] = useState<Boolean>(false);
  const initialValue = {
    list_status: Object.values(statusByRole).map((item: any) => {
      return item.code;
    }),
  };

  const onFinish = (value: any) => {
    const currentParams = { ...params };
    currentParams.page = defaultPage;
    const newQuery = {
      ...currentParams,
      list_status: value.list_status.toString(),
    };
    setParams(newQuery);
  };

  return {
    loading,
    setLoading,
    initialValue,
    onFinish,
  };
};

const useTerminalForm = (terminalId?: number) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [terminal, setTerminal] = useState<ITerminal>();
  const [services, setServices] = useState<IService[]>();
  const [fixedServices, setFixedServices] = useState<IService[]>();

  const fetch = async () => {
    if (terminalId) {
      setFetching(true);
      const result = await terminalApi.getTerminalById(terminalId);
      if (result && result.data) {
        const { data } = result;
        const services = convertServices(data.services || []);
        setServices(services);
        setFixedServices(services);
        setTerminal(data);
      }
      try {
      } finally {
        setFetching(false);
      }
    }
  };

  const reloadService = async () => {
    if (terminalId) {
      try {
        const result = await terminalApi.getTerminalById(terminalId);
        if (result && result.data) {
          const { data } = result;
          setServices(convertServices(data.services || []));
        }
      } finally {
      }
    }
  };

  const convertServices = (services: ITerminalService[]): IService[] => {
    return services.map(m => ({
      serviceId: 1,
      serviceCode: '',
      id: m.ts_id,
      authorization_letter: m.authorization_letter,
      service_id: m.service_id,
      service_code: m.code,
      approval_status: m.approval_status,
      code: m.code,
      ts_status: m.ts_status,
      is_default: m.is_default,
      extra_data: m.extra_data
        ? {
            ...m.extra_data,
            ...(m.extra_data.paymentSource
              ? {
                  paymentSource: (m.extra_data.paymentSource as any).split(','),
                }
              : {}),
          }
        : {},
      cost_sheets: m.cost_sheets?.map(
        c =>
          ({
            costSheetId: c.costSheetId,
            cardType: c.cardType,
            expiredAt: c.expiredAt
              ? moment(c.expiredAt, 'DD/MM/YYYY')
              : undefined,
          } as IServiceCostSheet)
      ),
      extra_doc: m.extra_doc,
      provider: m.provider,
      contract_number: m.contract_number,
      contract_brand: m.contract_brand,
      contract_sign_at: m.contract_sign_at,
      contract_doc: m.contract_doc,
      is_delete: false,
      department_id: m.department_id,
      staff_id: m.staff_id,
    }));
  };

  const createTerminal = async (data: any, service?: any) => {
    let resultData = {
      step: 0,
      terminalId: undefined,
    };
    try {
      setLoading(true);
      const result = await terminalApi.createTerminal(data);
      if (result && result.data) {
        resultData.step++;
        const { id } = result.data;
        await terminalApi.addServiceToTerminal({
          ...service,
          terminal_id: id.toString(),
        });
        resultData.terminalId = id;
      }
    } finally {
      setLoading(false);
    }
    return resultData;
  };

  const updateTerminal = async (
    id: number,
    data: any,
    service?: any,
    addedServices?: any
  ) => {
    let result;
    try {
      setLoading(true);
      result = await terminalApi.updateTerminal(id, data);
      if (result && service) {
        try {
          await terminalServiceApi.updateTidServiceWithoutNoti(id, service);
        } catch (e) {
          result = null;
        }
      }
      if (result && addedServices) {
        try {
          await terminalApi.addServiceToTerminal({
            ...addedServices,
            terminal_id: id.toString(),
          });
        } catch (e) {
          result = null;
        }
      }
    } catch (e) {
      result = null;
    } finally {
      setLoading(false);
    }
    return result;
  };

  const getDefaultTerminal = async (merchantId: number | undefined) => {
    if (merchantId) {
      const resultData = await terminalApi.searchTerminals({
        fields: 'merchant_id',
        operators: EOperators.EQ,
        values: merchantId,
        direction: EDirection.DESC,
      });
      if (resultData) {
        const { data } = resultData;
        return data[0];
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  const fetchById = async (terminalId: number) => {
    if (terminalId) {
      setFetching(true);
      const result = await terminalApi.getTerminalById(terminalId);
      if (result && result.data) {
        const { data } = result;
        return convertServices(data.services || []);
      }
      setFetching(false);
      return [];
    }
  };

  useEffect(() => {
    fetch().then();
  }, [terminalId]);

  return {
    loading,
    fetching,
    terminal,
    services,
    setServices,
    fixedServices,
    createTerminal,
    updateTerminal,
    getDefaultTerminal,
    fetchById,
    reloadService,
  };
};

const useTerminalActions = () => {
  const [loading, setLoading] = useState(false);

  const onChangeTerminalStatus = async (listId: any[], data: any) => {
    setLoading(true);
    let result = null;

    const callApi = async (id: any) => {
      try {
        const result = await terminalApi.changeApproveStatus(id, data);
        return {
          id,
          data: result,
        };
      } catch (e) {
        return {
          id,
          data: null,
        };
      }
    };

    const actions = listId.map(id => {
      return callApi(id);
    });
    try {
      result = await Promise.all(actions);
    } finally {
      setLoading(false);
    }
    return result;
  };
  return { loading, onChangeTerminalStatus };
};

const useTerminalService = () => {
  const createTermainalService = async (
    shopId: number,
    values: AnyObject,
    service: string
  ) => {
    switch (service) {
      case EService.QR:
        return await terminalService.createTerminalServiceQR({
          shopId,
          terminalCode: values.terminalCode.toUpperCase(),
          terminalName:
            values.terminalName && values.terminalName.toUpperCase(),
          billingCode: values.billingCode,
          mccVnpay: values.mccVnpay,
          paymentSource: values.paymentSource
            ? values.paymentSource.join(',')
            : null,
          beneficiaryId: values.beneficiaryId,
          contractNumber: values.contractNumber,
          contractName: values.contractName,
          contractSignedAt: moment(values.contractSignedAt).format(
            'YYYY-MM-DD'
          ),
          contractDoc: values.contractDoc,
          businessConditionCert: values.businessConditionCert,
          domainCert: values.domainCert,
          taxCert: values.taxCert,
          otherDoc: values.representerCert,
          identityCardFront: values.identityCardFront,
          identityCardBack: values.identityCardBack,
        });

      case EService.SPOS:
        return await terminalService.createTerminalServiceSPOS({
          shopId,
          terminalCode: values.terminalCode.toUpperCase(),
          terminalName:
            values.terminalName && values.terminalName.toUpperCase(),
          beneficiaryId: values.beneficiaryId,
          contractNumber: values.contractNumber,
          contractName: values.contractName,
          contractSignedAt: moment(values.contractSignedAt).format(
            'YYYY-MM-DD'
          ),
          contractDoc: values.contractDoc,
          businessConditionCert: values.businessConditionCert,
          evaluationRecord: values.evaluationRecord,
          otherDoc: values.representerCert,
          sposInstallment: values.sposInstallment
            ? values.sposInstallment === 'true'
            : undefined,
        });

      case EService.POS365:
        return await terminalService.createTerminalServicePOS365({
          shopId,
          servicePax: values.servicePax.toString(),
          totalDevice: values.totalDevice,
          pos365Url: values.pos365Url,
          receiptFiles: values.receiptFiles,
          contractDoc: values.contractDoc,
          businessConditionCert: values.businessConditionCert,
          otherDoc: values.representerCert,
        });

      case EService.SAPO:
        return await terminalService.createTerminalServiceSapo({
          shopId,
          servicePax: values.servicePax.toString(),
          receiptFiles: values.receiptFiles,
          contractDoc: values.contractDoc,
          businessConditionCert: values.businessConditionCert,
          otherDoc: values.representerCert,
        });

      case EService.SALE_MACHINE:
        return await terminalService.createTerminalServiceSaleDecive({
          shopId,
          totalDevice: values.totalDevice,
          receiptFiles: values.receiptFiles,
          contractDoc: values.contractDoc,
          businessConditionCert: values.businessConditionCert,
          otherDoc: values.representerCert,
        });
    }
  };

  return {
    createTermainalService,
  };
};

const useTerminalNoPay = () => {
  const [terminals, setTerminals] = useState<ITerminalNoPay[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingVerify, setLoadingVerify] = useState<boolean>(false);
  const [params, setParams] = useState<IGetTerminalNoPayParams>(
    DEFAULT_QUERY_TER_NOPAY_PARAMS
  );
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getTerminalNoPay = async () => {
    try {
      setLoading(true);
      const res = await terminalService.getTerminalNoPay(params);

      if (res && res.data) {
        setTerminals(res.data.terminalServices);
        const { pagination } = res.data;
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const onSearch = (values: IGetTerminalNoPayParams) => {
    let query = {
      ...pickBy(values, (e, key) => {
        return e !== undefined && e !== null;
      }),
    };
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onVerifyTerminal = async (
    id: number,
    payload: IVerifyTerminalNoPayPayload
  ) => {
    try {
      setLoadingVerify(true);
      await terminalService.verifyTerminal(id, payload);
      await getTerminalNoPay();
    } finally {
      setLoadingVerify(false);
    }
  };

  useEffect(() => {
    getTerminalNoPay();
  }, [params]);

  return {
    getTerminalNoPay,
    terminals,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    loadingVerify,
    setLoading,
    params,
    onVerifyTerminal,
  };
};

const useTerminalWaitingGrantedBank = () => {
  const defaultParams: IGetTerminalWaitingGrantedBankParams = {
    page: 1,
    pageSize: 10,
  };
  const [terminalWaitGrantBanks, setTerminalWaitGrantBanks] = useState<
    IGetTerminalWaitingGrantedBank[]
  >([]);
  const [params, setParams] = useState<IGetTerminalWaitingGrantedBankParams>(
    defaultParams
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getTerminalWaitingGrantedBank = async () => {
    try {
      setLoading(true);
      const res = await terminalService.getTerminalWaitingGrantedBank(params);
      if (res && res.data) {
        setTerminalWaitGrantBanks(res.data.terminalWaitGrantMidTidBanks);
        const { pagination } = res.data;
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const onSearch = (searchText: string) => {
    setParams({
      searchText: searchText.trim(),
      page: defaultPage,
      pageSize: params.pageSize,
    });
  };

  useEffect(() => {
    getTerminalWaitingGrantedBank();
  }, [params]);

  return {
    getTerminalWaitingGrantedBank,
    loading,
    terminalWaitGrantBanks,
    onSearch,
    paginationAnt,
    handleTableChange,
  };
};

export default {
  useTerminal,
  useTerminalForm,
  useTerminalActions,
  useTerminalWaitingFilter,
  useTerminalService,
  useTerminalNoPay,
  useTerminalWaitingGrantedBank,
};
